import {useNavigation} from '@react-navigation/native';
import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Alert,
  ImageBackground,
  Keyboard,
  Linking,
  ScrollView,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import ButtonComponent from '../../../../components/ButtonComponent/ButtonComponent';
import CheckBox from '../../../../components/CheckBox/CheckBox';
import Input from '../../../../components/Input/Input';
import {Images} from '../../../../constants/Images';
import {
  basePasswordSchema,
  confirmPasswordSchema,
  numberSchema,
  specialCharSchema,
} from '../../../../schemas/AuthSchema';
import {
  useRegisterFinishStore,
  useRegisterStore,
} from '../../../../store/RegisterStore';
import {ClientRegisterDataType} from '../../../../types/Auth';
import ShowMessagePass from './components/ShowMessagePass';
import {styles} from './styles';
import FontAwesome6 from 'react-native-vector-icons/FontAwesome6';
import {Colors} from '../../../../constants/Colors';
import AuthService from '../../../../services/AuthService/AuthService';
import {NavigationProps} from '../../../../navigation/Routes';
import {AppRoutes} from '../../../../navigation/Routes.types';
import {useLoadingStore} from '../../../../store/LoadingStore';
import { masks } from '../../../../utils/mask';

const RegisterForm = () => {
  const {navigate, goBack} = useNavigation<NavigationProps>();

  const [clientData, setClientData] = useState<ClientRegisterDataType>({
    user: '',
    email: '',
    confirmEmail: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const {showLoading, hideLoading} = useLoadingStore();
  const [checkPolitics, setCheckPolitics] = useState<boolean>(false);
  const [checkNotifications, setCheckNotifications] = useState<boolean>(false);
  const [errorsMessage, setErrorsMessage] = useState(['']);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const {registerData} = useRegisterStore();
  const {setRegisterFinishData} = useRegisterFinishStore();

  const finishRegister = async () => {
    // setLoading(true);
    try {
      showLoading();
      
      const response = await AuthService.postFinishRegister({
        clientCode: registerData.clientCode,
        username: registerData.cpfCnpj,
        password: clientData.password,
        confirmPassword: clientData.confirmPassword,
        phone: clientData.phone,
        email: clientData.email,
        confirmEmail: clientData.confirmEmail,
        notificationCheck: checkNotifications,
        policyCheck: true,
      });
      navigate(AppRoutes.TOKEN_SCREEN);
    } catch (error) {
      if (error.response && error.response.data) {
        const { details, message } = error.response.data;

        let errorMessage = message || 'Erro ao realizar cadastro. Tente novamente.';

        if (details && typeof details === 'object') {
            const detailMessages = Object.values(details)
            .flat()
            .join('\n');

            errorMessage = detailMessages || errorMessage;
        }
        Alert.alert('Opss...', String(errorMessage));
      } else {
        Alert.alert('Opss...', 'Algo deu errado, por favor tente novamente.');
      }
      console.error(error);
    } finally {
      hideLoading();
    }
    hideLoading();
    // setLoading(false);
  };

  const verifyDisabled = (password: string, confirmPasswordP: string) => {
    const resultBase = basePasswordSchema.safeParse({senha: password});
    const resultNumber = numberSchema.safeParse({senha: password});
    const resultSpecial = specialCharSchema.safeParse({senha: password});
    const confirmPassword = confirmPasswordSchema.safeParse({
      password: password,
      confirmPassword: confirmPasswordP,
    });
    if (!clientData.email || !clientData.confirmEmail) {
      Alert.alert('Preencher campos de E-mail!');
      setFieldErrors({
        email: !clientData.email ? 'Preencha o e-mail' : undefined,
        confirmEmail: !clientData.confirmEmail
          ? 'Preencha a confirmação de e-mail'
          : undefined,
      });
    } else if (clientData.email !== clientData.confirmEmail) {
      Alert.alert('Os E-mails não correspondem');
      setFieldErrors({confirmEmail: 'Os e-mails não correspondem'});
    } else if (
      !resultBase.success ||
      !resultNumber.success ||
      !resultSpecial.success
    ) {
      Alert.alert('Senha não obedece os requisitos');
      setFieldErrors({password: 'Senha não obedece os requisitos'});
    } else if (!confirmPassword.success) {
      Alert.alert('Senhas não correspondem');
      setFieldErrors({confirmPassword: 'Senhas não correspondem'});
    } else if (!checkPolitics) {
      Alert.alert('Necessário aceitar Política de privacidade!');
    } else {
      setFieldErrors({});
      setRegisterFinishData({
        username: registerData.cpfCnpj,
        email: clientData.email,
        phone: clientData.phone,
        confirmEmail: clientData.confirmEmail,
        password: clientData.password,
        confirmPassword: clientData.confirmPassword,
        notificationCheck: checkNotifications,
        clientCode: registerData.clientCode,
        cpfCnpj: registerData.cpfCnpj,
      });
      finishRegister();
    }
  };

  const validatePassword = (password: string) => {
    const errorsMessageArr: string[] = [];

    const resultBase = basePasswordSchema.safeParse({senha: password});
    if (!resultBase.success) {
      resultBase.error.errors.forEach(error => {
        errorsMessageArr.push(error.message);
      });
    }

    const resultNumber = numberSchema.safeParse({senha: password});
    if (!resultNumber.success) {
      resultNumber.error.errors.forEach(error => {
        errorsMessageArr.push(error.message);
      });
    }

    const resultSpecial = specialCharSchema.safeParse({senha: password});
    if (!resultSpecial.success) {
      resultSpecial.error.errors.forEach(error => {
        errorsMessageArr.push(error.message);
      });
    }

    setErrorsMessage(errorsMessageArr);

    return errorsMessageArr.length === 0;
  };

  return (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      <ImageBackground
        // source={Images.backgroundCoke}
        style={styles.imageBackground}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <View style={styles.container}>
            <TouchableOpacity
              onPress={goBack}
              style={{
                position: 'absolute',
                left: 40,
                top: 40,
                height: 30,
                width: 30,
              }}>
              <FontAwesome6
                name="arrow-left"
                color={Colors.GrayPallete.lightgrayThree}
                size={22}
              />
            </TouchableOpacity>
            <Text style={styles.title}>Finalize o cadastro</Text>
            {/* <Text style={styles.description}>
              Os campos assinalados com um * são obrigatórios
            </Text> */}
            <View style={styles.containerInput}>
              <Input
                title="Usuário"
                placeholder="123456789"
                editable={false}
                value={registerData.cpfCnpj}
              />
              <Input
                mt={20}
                title="Telefone"
                maxLength={14}
                placeholder="(61)99999-9999"
                value={clientData.phone}
                onChangeText={text => {
                  const phone = masks.maskPhone(text)
                  setClientData({...clientData, phone})
                }}
              />
              <Input
                title="E-mail"
                placeholder="e-mail@nomedaloja.com"
                autoCapitalize='none'
                mt={20}
                keyboardType="email-address"
                error={fieldErrors.email}
                onChangeText={text => {
                  setClientData({...clientData, email: text});
                  setFieldErrors(prev => ({...prev, email: undefined}));
                }}
              />
              <Input
                title="Confirme o E-mail"
                placeholder="e-mail@nomedaloja.com"
                autoCapitalize='none'
                mt={20}
                keyboardType="email-address"
                error={fieldErrors.confirmEmail}
                onChangeText={text => {
                  setClientData({...clientData, confirmEmail: text});
                  setFieldErrors(prev => ({...prev, confirmEmail: undefined}));
                }}
              />
              <Input
                title="Senha"
                placeholder="**************"
                mt={20}
                hasSecurity
                error={fieldErrors.password}
                onChangeText={text => {
                  setClientData({...clientData, password: text});
                  validatePassword(text);
                  setFieldErrors(prev => ({...prev, password: undefined}));
                }}
              />
              {clientData.password?.length > 0 && (
                <ShowMessagePass errorsMessage={errorsMessage} />
              )}
              <Input
                title="Confirme a Senha"
                placeholder="Confirme a Senha..."
                mt={20}
                hasSecurity
                error={fieldErrors.confirmPassword}
                onChangeText={text => {
                  setClientData({...clientData, confirmPassword: text});
                  setFieldErrors(prev => ({...prev, confirmPassword: undefined}));
                }}
              />
              <View style={styles.containerCheckBox}>
                <CheckBox
                  isCheck={checkPolitics}
                  onPress={() => setCheckPolitics(!checkPolitics)}
                  description="Concordo com a política."
                  mt={20}
                  alignSelf="flex-start"
                />
                <TouchableOpacity
                  onPress={() => {
                    const url =
                      'https://www.brasal.com.br/refrigerantes/pagina/aviso-de-privacidade';
                    //open PDF file in browser
                    Linking.openURL(url);
                  }}>
                  <Text
                    style={{
                      paddingLeft: 10,
                      paddingTop: 10,
                      paddingBottom: 5,
                      color: Colors.GrayPallete.lightgrayThree,
                      fontSize: 14,
                      fontFamily: 'TCCC-UnityHeadline Regular',
                      textDecorationLine: 'underline',
                    }}>
                    Ler política de privacidade.
                  </Text>
                </TouchableOpacity>
                <CheckBox
                  onPress={() => setCheckNotifications(!checkNotifications)}
                  isCheck={checkNotifications}
                  description="Quero receber notificações."
                  mt={5}
                  alignSelf="flex-start"
                />
              </View>
            </View>
            <View>
              {loading ? (
                <ActivityIndicator
                  color={Colors.RedPallete.red}
                  size={20}
                  style={{alignSelf: 'center'}}
                />
              ) : (
                <ButtonComponent
                  // disabled={verifyDisabled(clientData.password, clientData.confirmPassword)}
                  title="Finalizar cadastro"
                  onPress={() => {
                    // handleNavigateResendToken(clientData.password)
                    verifyDisabled(
                      clientData.password,
                      clientData.confirmPassword,
                    );
                  }}
                />
              )}
            </View>
          </View>
        </ScrollView>
      </ImageBackground>
    </TouchableWithoutFeedback>
  );
};

export default RegisterForm;
